/**
 * Post-pago: Stripe y MercadoPago deben llevar al usuario a la **misma** página de éxito
 * (por defecto `/muchas-gracias`). La ruta se define en Railway con una sola variable.
 *
 * - Stripe: `buildStripeSuccessReturnUrl(origin)` → incluye `session_id={CHECKOUT_SESSION_ID}`.
 * - MercadoPago: en el panel del plan de suscripción, configurá la URL de retorno a
 *   `{origin}{getPostPaymentSuccessPath()}` (MP añade `preapproval_id` según su flujo).
 *
 * El destino del **botón Telegram** (invite) sigue siendo por `plan_tier` Basic/VIP
 * (`muchasGraciasTelegramUrls.ts`); MP y Stripe comparten la misma URL de invite para el mismo tier.
 */

/**
 * Default canónico: post-pago el flujo membresía pasa por
 * `/portal/completar-perfil` (es) o `/portal/complete-profile` (en) → Clerk →
 * `/pending-approval` → `/mi-acceso`.
 * `/muchas-gracias` queda como override opcional vía
 * `VITE_MUCHAS_GRACIAS_SUCCESS_PATH` / `VITE_POST_PAYMENT_SUCCESS_PATH`.
 */
const DEFAULT_SUCCESS_PATH_ES = "/portal/completar-perfil";
const DEFAULT_SUCCESS_PATH_EN = "/portal/complete-profile";

export type PostPaymentLanguage = "en" | "es" | string | null | undefined;

/** Devuelve true cuando el idioma resuelve a inglés (ISO o BCP-47 como `en-US`). */
function isEnglish(language: PostPaymentLanguage): boolean {
  if (!language) return false;
  const lang = String(language).trim().toLowerCase();
  return lang === "en" || lang.startsWith("en-") || lang.startsWith("en_");
}

/** Path absoluto en este sitio (empieza con `/`). Config: `VITE_MUCHAS_GRACIAS_SUCCESS_PATH` o `VITE_POST_PAYMENT_SUCCESS_PATH`.
 *
 * Cuando `language` resuelve a inglés se prefiere la ruta en inglés
 * (`/portal/complete-profile`); para cualquier otro idioma se mantiene la canónica
 * en español (`/portal/completar-perfil`). El override por env sigue ganando.
 */
export function getPostPaymentSuccessPath(language?: PostPaymentLanguage): string {
  const raw = (
    import.meta.env.VITE_MUCHAS_GRACIAS_SUCCESS_PATH ||
    import.meta.env.VITE_POST_PAYMENT_SUCCESS_PATH ||
    ""
  )
    .toString()
    .trim();
  const fallback = isEnglish(language)
    ? DEFAULT_SUCCESS_PATH_EN
    : DEFAULT_SUCCESS_PATH_ES;
  if (!raw) return fallback;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return path.replace(/\/+$/, "") || fallback;
}

export type StripeSuccessReturnOpts = {
  language?: PostPaymentLanguage;
  /**
   * Query extra post-pago (sin session_id — se agrega siempre).
   * Membresía VIP: `{ plan_tier: "vip", from_vip: "1" }` para que
   * CompleteRegistration + Clerk metadata sepan el plan.
   */
  extraQuery?: Record<string, string | undefined>;
};

/**
 * Path + query para `success_url` / `return_url` de Stripe Checkout (relativo).
 * Ej.: `/portal/complete-profile?session_id={CHECKOUT_SESSION_ID}&provider=stripe&lang=en&plan_tier=vip&from_vip=1`
 *
 * Flujo: Stripe (pago) → esta URL → Clerk (sign-in/up si falta sesión) →
 * completar perfil → `/pending-approval`.
 */
export function buildStripeSuccessReturnRelative(
  languageOrOpts?: PostPaymentLanguage | StripeSuccessReturnOpts,
): string {
  const opts: StripeSuccessReturnOpts =
    languageOrOpts && typeof languageOrOpts === "object" && !Array.isArray(languageOrOpts)
      ? languageOrOpts
      : { language: languageOrOpts as PostPaymentLanguage };

  const p = getPostPaymentSuccessPath(opts.language);
  // session_id MUST stay as literal `{CHECKOUT_SESSION_ID}` (not URL-encoded).
  // Stripe substitutes that exact string on redirect (hosted success_url + embedded return_url).
  const parts: string[] = [
    "session_id={CHECKOUT_SESSION_ID}",
    "provider=stripe",
  ];

  const langCode = opts.language
    ? String(opts.language).trim().toLowerCase().split(/[-_]/)[0]
    : "";
  if (langCode) {
    parts.push(`lang=${encodeURIComponent(langCode)}`);
  }

  if (opts.extraQuery) {
    for (const [k, v] of Object.entries(opts.extraQuery)) {
      if (!k || v == null || v === "") continue;
      if (k === "session_id" || k === "provider") continue;
      parts.push(
        `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
      );
    }
  }

  const join = p.includes("?") ? "&" : "?";
  return `${p}${join}${parts.join("&")}`;
}

/** URL absoluta lista para enviar a Stripe como `successUrl` / embedded `return_url`. */
export function buildStripeSuccessReturnUrl(
  origin: string,
  languageOrOpts?: PostPaymentLanguage | StripeSuccessReturnOpts,
): string {
  const base = origin.replace(/\/+$/, "");
  const rel = buildStripeSuccessReturnRelative(languageOrOpts);
  return `${base}${rel.startsWith("/") ? rel : `/${rel}`}`;
}

/** Query canónica de membresía VIP post-pago (Clerk profile + pending-approval). */
export const STRIPE_VIP_MEMBERSHIP_SUCCESS_QUERY = {
  plan_tier: "vip",
  from_vip: "1",
} as const;
